export interface Question {
  title: string
  content: string | JSX.Element
}

export interface QuestionSection {
  section: string
  questions: Question[]
}

export interface FromTo {
  from: string
  to: string
}

export const fromToData: FromTo[] = [
  {
    from: 'Begin and end with brand',
    to: 'People-first Product-centric rigor',
  },
  {
    from: 'Fluffy conceptual language',
    to: 'Language fiercely rooted in data',
  },
  {
    from: 'Creative led and decided',
    to: 'Leveraging enterprise expertise',
  },
]
export const mockQuestions: QuestionSection[] = [
  {
    section: 'GENERAL FRAMEWORK',
    questions: [
      {
        title: 'What is BVA 2.0?',
        content:
          'BVA 2.0 is the evolved version of the Brand Vision Architecture framework: a people-first, product-centric approach that fuses Human Tension, Product Edge, and Brand Edge into a single Meaningful Brand Role. It is designed to strengthen brand positioning, spark innovation, and align teams worldwide around a clear brand narrative.',
      },
      {
        title: 'Why did we evolve from the previous BVA?',
        content:
          'The update ensures our framework reflects today’s realities: grounding creativity in data, sharpening product differentiation, and aligning with human truths. It moves us away from siloed thinking to fully integrated, cross-functional collaboration.',
      },
      {
        title: 'Who is this framework for?',
        content:
          'Everyone working on TCCC brand teams from Category Marketeers, Human Insight, Design or R&D. Whether you’re refreshing a mature brand or building something new, BVA 2.0 ensures we speak with one voice worldwide.',
      },
      {
        title: 'Do I have to complete all three parts (Human Tension, Product Edge, Brand Edge) every time?',
        content:
          'Yes. Even if you’re refreshing one pillar, you must review the others for alignment: the power of BVA 2.0 lies in the fusion of all three.',
      },
      {
        title: 'Where can I find good examples of BVA templates from other brands?',
        content: (
          <p>
            Explore the <button className="link">BVA Frameworks</button>
            page to view current and previous BVAs.
          </p>
        ),
      },
    ],
  },
  {
    section: 'THE PROCESS',
    questions: [
      {
        title: 'Where do I begin with BVA 2.0?',
        content:
          'Start with a quick Brand Assessment to clarify whether you need a full redo or a focused refresh. This guides the depth of work required and team setup.',
      },
      {
        title: 'Who leads each pillar?',
        content: (
          <p>
            The Brand Lead (+PMO if applicable) drives the overall BVA process, but each stream will have its own
            leadership:{' '}
            <ul className="list-disc ml-6">
              <li>People (Human Tension): Human Insights Lead</li>
              <li>Product (Product Edge): Innovation Lead</li>
              <li>Brand (Brand Edge): Global Brand Lead</li>
              <li>Shared ownership with clear accountability per pillar</li>
            </ul>
          </p>
        ),
      },
      {
        title: 'Do we have to work in sprints?',
        content: (
          <p>
            You can choose between two modes:{' '}
            <ul className="list-disc ml-6">
              <li>
                Async Exploration with Moment of Fusion (best for refreshes, ​strong external partner network​s and
                geographically dispersed teams​)
              </li>
              <li>
                End-to-End Co-Creation Sprint (best for redo/heavy refresh​es, dedicated team availability​ and
                preference for real-time collaboration)
              </li>
            </ul>
          </p>
        ),
      },
      {
        title: 'How long does the process take?',
        content:
          'Typically, 4–8 weeks of pre-work followed by a 2-day aligment sprint (Async Exploration with Moment of Fusion​) or 1 full week of co-creation sprint (End-to-End Co-creation​) to finalize and integrate outputs.',
      },
      {
        title: 'What happens after we complete the framework?',
        content:
          'Outputs feed directly into innovation, communications, and experience briefs, ensuring consistency across touchpoints and markets.',
      },
    ],
  },
  {
    section: 'HUMAN TENSION',
    questions: [
      {
        title: 'What is Human Tension?',
        content:
          'Human Tension is the unresolved tension or conflict in people’s lives that your brand can help resolve, it relies on ongoing beyond functional needs to uncover deeper emotional drivers.',
      },
      {
        title: 'Why is Human Tension important?',
        content:
          'It keeps the brand rooted in real life, ensuring our communications and innovations speak to what truly matters to people rather than category conventions.',
      },
      {
        title: 'How do I identify a strong Human Tension?',
        content:
          'Look for tensions that are universal enough to scale yet specific enough to feel personal. They should be timeless, emotionally charged, and credibly addressable by your brand.',
      },
      {
        title: 'What information should I gather to uncover Human Tension?',
        content:
          'Start with human insights, such as: beliefs, values, behaviors, and unmet needs. Use tools like need-state deep dives, consumer immersions, and psychographic data.',
      },
      {
        title: 'What if multiple tensions emerge?',
        content:
          'Focus on the tension that best aligns with your brand’s strengths and future growth ambition. Be honest about whether you are being bold enough in naming tensions you can genuinely help resolve.',
      },
    ],
  },
  {
    section: 'PRODUCT EDGE',
    questions: [
      {
        title: 'What is a Product Edge?',
        content:
          'Product Edge is the distinctive, irresistible point of difference that consumers crave and competitors envy.',
      },
      {
        title: 'What isn’t a Product Edge?',
        content:
          'A Product Edge isn’t a catch-all, so keep it focused, product-centric and reflective of what consumers actually care about. If it feels like it’s wandering, simplify and move any extra details to their rightful place in the BVA 2.0.',
      },
      {
        title: 'What makes a strong Product Edge?',
        content:
          'An exemplary Product Edge is a feature or benefit that’s 100% true to your brand, stands out against competition, and meets consumers’ needs.',
      },
      {
        title: 'What information should I collect before defining my Product Edge? ',
        content:
          'Craft it from the “product truths” in the BVA template. Think RTBs, functional benefits, sensorial cues, product & consumer insights.',
      },
    ],
  },
  {
    section: 'BRAND EDGE',
    questions: [
      {
        title: 'What is Brand Edge?',
        content:
          'Brand Edge is what makes your brand distinctive in people’s minds: the emotional territory, personality, and assets that set you apart from other brands in the category.',
      },
      {
        title: 'How is Brand Edge different from Product Edge?',
        content:
          'While Product Edge is rooted in what the product delivers, Brand Edge lives in how the brand behaves, feels, and shows up emotionally and culturally.',
      },
      {
        title: 'What makes a strong Brand Edge?',
        content:
          'It’s authentic to your brand’s history yet forward-looking, distinctive from competitors, and builds saliency by tapping into recognizable assets and emotional benefits.',
      },
      {
        title: 'What information should I gather to define Brand Edge?',
        content:
          'Audit your distinctive brand assets, emotional benefits, memory structures, and heritage cues. Use equity studies, semiotic analyses, and cultural audits for depth.',
      },
    ],
  },
  {
    section: 'MEANINGFUL BRAND ROLE',
    questions: [
      {
        title: 'What is the Meaningful Brand Role?',
        content:
          'It’s the unifying idea at the intersection of Human Tension, Product Edge, and Brand Edge. It defines the role the brand plays in an individual’s life by resolving the Human Tension.',
      },
      {
        title: 'Why is the Meaningful Brand Role important?',
        content:
          'It connects what matters to people with what only our product and brand can deliver, ensuring our positioning is both emotionally relevant and product-driven',
      },
      {
        title: 'When do we define the Meaningful Brand Role?',
        content:
          'It comes after the People, Product, and Brand workstreams are complete, during the sprint or fusion sessions where all inputs are combined.',
      },
      {
        title: 'How is the Meaningful Brand Role used after it is defined?',
        content:
          'Once defined, the Meaningful Brand Role is used as a pressure-test and decision lens across the organization. It is applied to validate priority demand spaces, assess the relevance and distinctiveness of experience design, and inspire product innovation that is both on-brand and commercially viable. Finally, it serves as a tool to align stakeholders and secure approval across OU leadership, sponsors, and GMO.',
      },
    ],
  },
  {
    section: 'PRACTICALITIES',
    questions: [
      {
        title: 'Where do I find the templates?',
        content: (
          <p>
            All official templates live in the{' '}
            <a
              className="font-bold underline"
              href="https://app.mural.co/t/reborrn0190/m/reborrn0190/1741803875130/917b26cb6886cf897e1b7145046bcdb99e8249d8"
            >
              BVA 2.0 Mural board
            </a>{' '}
            — use these to capture final outputs, not local versions.
          </p>
        ),
      },
      {
        title: 'Can I see examples from other brands?',
        content: (
          <p>
            Explore the
            <a className="subtitle-s font-bold underline" href="/bvaframework">
              BVA Frameworks page
            </a>{' '}
            to view current and previous BVAs.
          </p>
        ),
      },
      {
        title: 'Who signs off on the final BVA?',
        content: (
          <p>
            The Global GCP is the sponsor of the entire process and must approve the outcome before it goes to the CMO
            for final signoff.​ Finally, the Global CMO gives the final approval before global release.
          </p>
        ),
      },
      {
        title: 'Who can I contact for additional support?',
        content: (
          <p>
            Reach out to the BVA 2.0 Project Team:{' '}
            <ul>
              <li>
                <a className="font-bold underline" href="mailto:skakouros@coca-cola.com">
                  Stavros Kakouros
                </a>
              </li>
              <li>
                <a className="font-bold underline" href="mailto:afabbri@coca-cola.com">
                  Annalisa Fabbri
                </a>
              </li>
              <li>
                <a className="font-bold underline" href="mailto:mansari@coca-cola.com">
                  Maheen Ansari
                </a>
              </li>
              <li>
                <a className="font-bold underline" href="mailto:skuper@coca-cola.com">
                  Shayna Kuper
                </a>
              </li>
            </ul>
          </p>
        ),
      },
    ],
  },
]

export const BVAphases = [
  {
    title: 'Brand Assessment',
    subtitle: 'Determine where your brand stands today.',
    hover: (
      <div className="text-[12px]">
        <p className="text-[14px] font-bold uppercase">are you due for a refresh or a redo?</p>
        <p className="my-2">Determine where your brand stands today to identify the next steps:</p>
        <ul>
          <li> Global brand lead kicks off BVA performance review against new framework requirements</li>
          <li>Determine refresh vs. redo approach</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Project Set-Up',
    subtitle: 'Establish the right foundation for success.',
    hover: (
      <div className="text-[12px]">
        <p className="text-[14px] font-bold uppercase">Setting the foundation for success.</p>
        <p className="my-2">Establish the right team, assign roles, and define timelines and milestones:</p>
        <ul>
          <li> Assign SME workstream owners (People, Product, Brand)</li>
          <li> Define core & extended team members per workstream</li>
          <li> Set timeline & key milestones</li>
          <li> Audit existing knowledge</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Working Mode Selection',
    subtitle: 'Tailor the process to your team’s needs.',
    hover: (
      <div className="text-[12px]">
        <p className="text-[14px] font-bold uppercase">Tailor the process to your team's needs.</p>
        <p className="my-2">Choose between two collaborative working models:</p>
        <ul>
          <li>Async Exploration with Moment of Fusion</li>
          <li>End-to-End Co-creation</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Insights Gathering',
    subtitle: 'Gather data and knowledge to fuel the journey.',
    hover: (
      <div className="text-[12px]">
        <p className="text-[14px] font-bold uppercase">Get inspired by data and consumer immersion.</p>
        <p className="my-2">Gather insights and knowledge to fuel the journey:</p>
        <ul>
          <li>Commission critical research</li>
          <li>Run team immersions</li>
        </ul>
      </div>
    ),
  },
  {
    title: 'Development',
    subtitle: 'Unite and finalize your BVA for meaningful results.',
    hover: (
      <div className="text-[12px]">
        <p className="text-[14px] font-bold uppercase">Bring it all together in a meaningful way.</p>
        <p className="my-2">Unite teams and finalize your BVA through co-creation:</p>
        <ul>
          <li>Finalize People, Product & Brand templates through co-creation</li>
          <li>Ensure cross-stream alignment</li>
          <li>Define Meaningful Brand Role</li>
          <li>Validate with stakeholders</li>
        </ul>
      </div>
    ),
  },
]
